<template>
  <div v-if="displayWorkflowFallbackMessage" data-qa="preview-content-email">
    <div class="fallback-msg-ct">
      {{ $t('CampaignForm_Preview_ContentMessageDetails_NoMessages') }}
    </div>
  </div>
  <div v-else data-qa="preview-content-email">
    <preview-send-dialog
      :dialog-visible.sync="showSendPreview"
      :preview="previewSendData"
      v-if="showSendPreview"
    />
    <div class="flexbox">
      <div class="device-selector-container">
        <el-select
          v-model="selectedDevice"
          value-key="name"
          @change="getPreview()"
          data-qa="preview-email-device-select"
        >
          <el-option
            v-for="device in devices"
            :key="device.name"
            :label="$t(device.name)"
            :value="device"
          />
        </el-select>
      </div>
      <form-item class="p-top-27">
        <toggle
          v-model="highlight"
          @change="getPreview()"
          :active-text="$t('CampaignForm_Preview_Highlight')"
          :inactive-text="$t('CampaignForm_Preview_Highlight')"
          data-qa="preview-email-highlight-toggle"
        />
      </form-item>
    </div>
    <div v-loading="loading" data-qa="preview-email-body">
      <div class="message-details-ct" data-qa="preview-email-message-details">
        <div class="row">
          <span class="subject">
            {{ $t('CampaignForm_Preview_ContentMessageDetails_Email_Subject') }}
          </span>
          <span
            class="subject-details"
            v-html="content.subject"
            data-qa="preview-email-subject"
          />
        </div>
        <div>
          <span class="from">
            {{ $t('CampaignForm_Preview_ContentMessageDetails_Email_From') }}
          </span>
          <span
            class="from-details"
            v-html="content.from_name"
            data-qa="preview-email-from-name"
          />
          <span
            v-if="content.from_address"
            class="from-details-extended"
            data-qa="preview-email-from-address"
          >
            <!-- eslint-disable-next-line -->
            <{{ content.from_address }}>
          </span>
        </div>
      </div>

      <div
        ref="deviceContainer"
        class="device-container"
        data-qa="preview-email-device-container"
      >
        <div :style="iframeStyle" class="device-frame">
          <iframe
            v-show="!loading"
            ref="iframeRef"
            :srcdoc="getHtml"
            class="email-preview"
            @load="isIframeLoaded"
            data-qa="preview-email-iframe"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import { PreviewChannelEnum } from '@/modules/campaign-manage/enum/PreviewChannelEnum'
import { EventBus } from '@/core/EventBus'
import _ from 'lodash'
import FormItem from '@/common/components/form/FormItem.vue'
import Toggle from '@/common/components/form/Toggle.vue'
import { Message } from 'element-ui'
import PreviewSendDialog from '@/modules/campaign-manage/components/preview/PreviewSendDialog.vue'
import {
  processCustomAttributes,
  normaliseUnmetConditions
} from '@/modules/campaign-manage/helpers/PreviewAttributeHelper'

export default {
  name: 'PreviewContentEmail',
  components: { Toggle, FormItem, PreviewSendDialog },
  props: {
    model: {
      type: Object,
      required: true
    },
    variantKey: {
      type: String,
      required: true
    },
    workflowAction: {
      type: Object,
      required: false,
      default: () => ({})
    },
    language: {
      type: String,
      required: false
    },
    deviceKey: {
      type: Number,
      required: false
    },
    profileId: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Full profile object corresponding to `profileId` — used by the test-send
     * dialog to render a human label for the selected profile in its description.
     */
    selectedProfile: {
      type: Object,
      required: false,
      default: null
    },
    customAttributes: {
      type: Object,
      required: false,
      default: null
    }
  },
  mounted() {
    this.selectedDevice = this.devices[0]
    if (this.displayWorkflowFallbackMessage) {
      this.loading = false
      EventBus.$emit('set-profile-attributes', {})
      EventBus.$emit('set-unmet-attribute-conditions', {})
    } else {
      this.getPreview()
    }
    EventBus.$on('showSendPreview', this.onShowSendPreview)
  },
  beforeDestroy() {
    EventBus.$off('showSendPreview', this.onShowSendPreview)
  },
  data() {
    return {
      devices: [
        {
          name: 'CampaignForm_Preview_DeviceSelect_Desktop',
          width: 600
        },
        {
          name: 'CampaignForm_Preview_DeviceSelect_Mobile',
          width: 318
        }
      ],
      selectedDevice: {},
      iframeEl: null,
      loading: true,
      content: {},
      highlight: 1,
      showSendPreview: false
    }
  },
  computed: {
    previewSendData() {
      return {
        campaign: this.model,
        message_type: channelTypes.email,
        variant_key: this.variantKey,
        workflow_action: this.workflowAction,
        language: this.language,
        profile_id: this.profileId,
        selected_profile: this.selectedProfile,
        customAttributes: this.processCustomAttributes(this.customAttributes)
      }
    },
    displayWorkflowFallbackMessage() {
      return Boolean(this.model.workflow) && _.isEmpty(this.workflowAction)
    },
    iframeStyle() {
      this.$nextTick(() => {
        if (this.iframeEl) {
          this.iframeEl.style.height = `400px`
          let height = this.iframeEl.contentWindow.document.documentElement
            .scrollHeight
          this.iframeEl.style.height = `${height}px`
        }
      })

      return { width: `${this.selectedDevice.width}px` }
    },
    contentHighlightForFallback() {
      return (
        'mark.substituted-content-fallback { background-color: #fff3cc } ' +
        'mark.substituted-content-fallback * { background-color: inherit } '
      )
    },
    contentHighlightForNoFallback() {
      return (
        'mark.substituted-content-no-fallback { background-color: #fecdd3; } ' +
        `mark.substituted-content-no-fallback:empty { display: inline-block; width: ${this.selectedDevice.width}px; height: 18px; } ` +
        'mark.substituted-content-no-fallback * { background-color: inherit; } '
      )
    },
    getHtml() {
      // Injecting the Jquery, JqueryUI, JqueryCss as well as JqueryToolTip
      // Doing it in the isIframeLoaded is not working as it's too quick to register that all CDN's are loaded
      // This will add the scrip tags for each CDN before loading it onto the iframe
      const parser = new DOMParser()
      const html = parser.parseFromString(this.content.html, 'text/html')

      const jquery = document.createElement('script')
      jquery.src =
        'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.7.1/jquery.min.js'
      const jqueryUi = document.createElement('script')
      jqueryUi.src =
        'https://cdnjs.cloudflare.com/ajax/libs/jqueryui/1.14.0/jquery-ui.min.js'
      const jqueryCss = document.createElement('link')
      jqueryCss.rel = 'stylesheet'
      jqueryCss.href =
        'https://cdnjs.cloudflare.com/ajax/libs/jqueryui/1.14.0/themes/base/jquery-ui.min.css'
      const jqueryToolTip = document.createElement('script')
      jqueryToolTip.innerHTML = `$( document ).tooltip();`

      html.head.append(jquery, jqueryUi, jqueryCss, jqueryToolTip)
      return html.documentElement.outerHTML
    }
  },
  watch: {
    customAttributes: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.getPreview()
        }
      },
      deep: true
    }
  },
  methods: {
    processCustomAttributes,
    onShowSendPreview(payload) {
      if (!payload || payload.channel === PreviewChannelEnum.EMAIL) {
        this.showSendPreview = true
      }
    },
    isIframeLoaded(e) {
      this.iframeEl = e.target
      let height = e.target.contentWindow.document.documentElement.scrollHeight
      e.target.style.height = `${height}px`

      const substitutedContentCssRule = document.createElement('style')
      substitutedContentCssRule.textContent =
        '.substituted-content { text-decoration-color: #1D87C9 !important; text-decoration: underline; text-decoration-style: dashed; }' +
        '.substituted-empty-content, .substituted-content:empty { border-left: 1px dotted #ED1D27; }' +
        'div.substituted-content { border-bottom: 1px dotted #1D87C9 !important; }' +
        'mark.substituted-content-met { background-color: #b5dcf4 } ' +
        'mark.substituted-content-met * { background-color: inherit } ' +
        this.contentHighlightForFallback +
        this.contentHighlightForNoFallback
      this.iframeEl.contentDocument.head.appendChild(substitutedContentCssRule)
    },
    getPreview() {
      this.loading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaign-messages/preview`,
          data: {
            campaign: this.model,
            message_type: channelTypes.email,
            variant_key: this.variantKey,
            workflow_action: this.workflowAction,
            language: this.language,
            profile_id: this.profileId,
            customAttributes: this.processCustomAttributes(
              this.customAttributes
            ),
            preview_mode: this.highlight
          }
        })
      )
        .then(({ data }) => {
          if (this._isDestroyed) return
          this.content = data['message']
          EventBus.$emit('set-profile-attributes', data.attributes)
          if (!this.customAttributes && this.profileId) {
            EventBus.$emit('set-profile-id', this.profileId)
          }
          EventBus.$emit(
            'set-unmet-attribute-conditions',
            normaliseUnmetConditions(data['unmetConditions'])
          )
          this.injectIframeStyles()
        })
        .catch(error => {
          Message.closeAll()
          this.$message({
            message: error.message,
            type: 'error',
            duration: 5000
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    injectIframeStyles() {
      // This function is used to cover an edge case, when firing @load="isIframeLoaded" takes too much time, e g img can't load
      setTimeout(() => {
        if (this.$refs.iframeRef && this.$refs.iframeRef.contentDocument) {
          const substitutedContentCssRule = document.createElement('style')
          substitutedContentCssRule.textContent =
            '.substituted-content { text-decoration-color: #1D87C9 !important; text-decoration: underline; text-decoration-style: dashed; }' +
            '.substituted-empty-content, .substituted-content:empty { border-left: 1px dotted #ED1D27; }' +
            'div.substituted-content {border-bottom: 1px dotted #1D87C9 !important;} ' +
            'mark.substituted-content-met { background-color: #b5dcf4 } ' +
            'mark.substituted-content-met * { background-color: inherit } ' +
            this.contentHighlightForFallback +
            this.contentHighlightForNoFallback
          this.$refs.iframeRef.contentDocument.head.appendChild(
            substitutedContentCssRule
          )
        }
      }, 2000)
    }
  }
}
</script>

<style scoped lang="scss">
.flexbox {
  display: flex;
  justify-content: space-between;
}
.fallback-msg-ct {
  margin-top: 30%;
  margin-right: 50%;
  margin-left: 50%;
  width: 300px;
  font-size: 16px;
}

.device-selector-container {
  padding: 20px 0;
}

.message-details-ct {
  padding: 20px;
  font-size: 14px;
  background-color: #ffffff;

  /deep/ .substituted-content {
    text-decoration-color: #1d87c9 !important;
    text-decoration: underline;
    text-decoration-style: dashed;
  }
  /deep/ .substituted-content:empty {
    border-left: 1px dotted #ed1d27;
  }
}

.subject-details,
.from-details {
  margin-left: 20px;
  font-weight: 600;
}

.from-details {
  margin-left: 61px;
}

.from-details-extended {
  margin-left: 20px;
  color: #7e8596;
}

.device-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  flex: 1;
  box-shadow: 0 4px 20px 0 #0000001a;
  margin-bottom: 91px;
  background-color: #fff;
  border-radius: 8px;

  /* Hide scrollbar for Chrome, Safari and Opera */
  .device-frame::-webkit-scrollbar {
    display: none;
  }

  .device-frame {
    position: relative;
    min-height: 400px;
    background-color: #fff;
    overflow: scroll;
    box-sizing: border-box;
    width: 100%;
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1));
    margin-top: 10px;
    margin-bottom: 10px;
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    .email-preview {
      background-color: #ffffff;
      width: 100%;
      height: 100%;
      border: 0;
      filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1));
      box-sizing: border-box;
      min-height: 400px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/PreviewContentEmail.vue